import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Zap,
  Activity,
  Calculator,
  TrendingUp,
  RefreshCw,
  FileSpreadsheet,
  CheckCircle,
  History,
  Loader2,
  AlertTriangle,
  AlertCircle,
  Info,
  Lock,
  Clock,
  RotateCcw,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import OfferCalendar from './components/OfferCalendar';
import MultiDayOfferCharts from './components/MultiDayOfferCharts';
import OfferAdjuster from './components/OfferAdjuster';
import FinalExcelDialog from './components/FinalExcelDialog';
import WetSeasonSwitch from './components/WetSeasonSwitch';
import { poolSourceLabel } from '@/pages/JobRuns/utils/pool';
import QuickAnalytics from './components/shared/QuickAnalytics';
import { DayOffer, QuickAnalytic } from './types';
import {
  adjustPayload,
  bidToDayOffer,
  dayPoolBudget,
  exportFileName,
  naerasWriteErrorMessage,
  NaerasErrorCodes,
  nextDate,
  parseNaerasError,
} from './utils/naerasBid';
import useNotify from '../../hooks/useNotify';
import { format, parseISO, subDays } from 'date-fns';
import { useAppDispatch } from '@/data/store/utils/redux';
import {
  adjustNaerasBidAsync,
  calculateNaerasBidsAsync,
  exportNaerasBidsAsync,
  finalizeNaerasBidsAsync,
  getAvailablePlanLedgersAsync,
  getNaerasBidAsync,
  getNaerasCalendarAsync,
  getNaerasPoolAsync,
  importNaerasBidsAsync,
} from '@/data/store/actions';
import { getNaerasBidsMap, getNaerasCalendar, getNaerasPool } from '@/data/store/selectors';
import { TNaerasBid } from '@/data/types';

/**
 * The one error code this page reads rather than leaves to the global handler:
 * it separates "this day has no bid yet" from "this day failed to load", and
 * that distinction drives an inline hint, not a toast.
 */
const BID_NOT_FOUND = NaerasErrorCodes.NOT_FOUND;

/** Athens time the nightly job locks everything not final yet. */
const AUTO_FINALIZE_TIME = '23:30';

/** How long to wait after the last +/− press before sending the PATCH. */
const ADJUST_DEBOUNCE_MS = 700;

/**
 * Where one selected day stands.
 *
 * `missing` is not a failure: the day simply has no bid yet, and the answer is
 * the Calculate button rather than an error.
 */
type DateStatus =
  | { state: 'loading' }
  | { state: 'loaded' }
  | { state: 'missing'; message?: string }
  | { state: 'error'; message: string };

/** Triggers the browser download of a blob under a given name. */
function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Percentage change, formatted the way the KPI cards read. */
function changeAgainst(current: number, previous: number | null, label: string) {
  if (previous === null || previous === 0) {
    return { change: `Χωρίς σύγκριση με ${label}`, changeType: 'positive' as const };
  }

  const pct = ((current - previous) / previous) * 100;

  return {
    change: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% ως προς ${label}`,
    changeType: (pct >= 0 ? 'positive' : 'negative') as 'positive' | 'negative',
  };
}

export const CalculatePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast } = useNotify();

  const calendar = useSelector(getNaerasCalendar);
  const bidsMap = useSelector(getNaerasBidsMap);
  const pool = useSelector(getNaerasPool);

  const [searchParams] = useSearchParams();
  const dateFromQuery = searchParams.get('date');
  const [selectedDates, setSelectedDates] = useState<string[]>(
    dateFromQuery ? [dateFromQuery] : []
  );
  // Unsaved +/− edits, keyed by date, overlaid on the server rows
  const [drafts, setDrafts] = useState<Record<string, DayOffer>>({});
  // Active day selected for tuning in OfferAdjuster
  const [activeAdjusterDate, setActiveAdjusterDate] = useState<string>('');
  // State for Final Excel Modal
  const [isExcelDialogOpen, setIsExcelDialogOpen] = useState(false);
  // Perk state
  const [hasNaerasPerk, setHasNaerasPerk] = useState<boolean | null>(null);
  // How each selected day's load ended, so the page can say so in place
  const [dateStatuses, setDateStatuses] = useState<Record<string, DateStatus>>({});

  const [isCalculating, setIsCalculating] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Days the last finalize calculated afresh: their numbers may have moved
  const [recalculatedDates, setRecalculatedDates] = useState<string[]>([]);

  const adjustTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Unselected days already asked for, so a 404 isn't requested again
  const requestedGapDates = useRef(new Set<string>());

  const yesterdayStr = calendar
    ? format(subDays(parseISO(calendar.today), 1), 'yyyy-MM-dd')
    : format(subDays(new Date(), 1), 'yyyy-MM-dd');
  const yesterdayLabel = format(parseISO(yesterdayStr), 'd/M/yyyy');

  useEffect(() => {
    const attemptGetAvailablePlanLedgers = async () => {
      try {
        const ledger = await dispatch(getAvailablePlanLedgersAsync({})).unwrap();
        setHasNaerasPerk(ledger?.perks?.naeras === true);
      } catch {
        setHasNaerasPerk(false);
      }
    };

    attemptGetAvailablePlanLedgers();
  }, [dispatch]);

  // The backend owns the calendar window and the hybrid pool.
  useEffect(() => {
    if (!hasNaerasPerk) return;

    dispatch(getNaerasCalendarAsync())
      .unwrap()
      .catch(() => undefined);
    dispatch(getNaerasPoolAsync())
      .unwrap()
      .catch(() => undefined);
  }, [dispatch, hasNaerasPerk]);

  /**
   * Loads one day's bid and classifies the answer.
   *
   * A 404 with NAERAS_BID_NOT_FOUND is the normal answer for a day nobody has
   * calculated yet, so it becomes an inline hint rather than an error. Anything
   * else is a real failure and gets its own inline message; neither goes to the
   * global error toast, which is why the thunk is dispatched `silent`.
   */
  const loadBid = useCallback(
    (dateStr: string) => {
      setDateStatuses((prev) => ({ ...prev, [dateStr]: { state: 'loading' } }));

      return dispatch(getNaerasBidAsync({ targetDate: dateStr, silent: true }))
        .unwrap()
        .then(() => {
          setDateStatuses((prev) => ({ ...prev, [dateStr]: { state: 'loaded' } }));
        })
        .catch((error) => {
          const { code, message } = parseNaerasError(error);

          setDateStatuses((prev) => ({
            ...prev,
            [dateStr]:
              code === BID_NOT_FOUND || !code
                ? { state: 'missing', message: message || undefined }
                : { state: 'error', message: message || 'Αποτυχία φόρτωσης της προσφοράς.' },
          }));
        });
    },
    [dispatch]
  );

  // Load whatever has already been calculated for the selected days.
  useEffect(() => {
    if (!hasNaerasPerk || selectedDates.length === 0) return;

    selectedDates.forEach((dateStr) => {
      if (bidsMap[dateStr] || dateStatuses[dateStr]) return;

      loadBid(dateStr);
    });

    if (!selectedDates.includes(activeAdjusterDate)) {
      setActiveAdjusterDate(selectedDates[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates, hasNaerasPerk]);

  // Days between tomorrow and the first selected one draw on the same pool, so
  // a later day can't say what is left of it without their bids.
  useEffect(() => {
    if (!hasNaerasPerk || !calendar || selectedDates.length === 0) return;

    const firstSelected = [...selectedDates].sort()[0];

    for (let date = calendar.min_date; date < firstSelected; date = nextDate(date)) {
      if (bidsMap[date] || requestedGapDates.current.has(date)) continue;

      requestedGapDates.current.add(date);
      dispatch(getNaerasBidAsync({ targetDate: date, silent: true }))
        .unwrap()
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNaerasPerk, calendar, selectedDates]);

  // Yesterday's bid, for the KPI deltas. Absent for days never calculated.
  useEffect(() => {
    if (!hasNaerasPerk || bidsMap[yesterdayStr]) return;

    dispatch(getNaerasBidAsync({ targetDate: yesterdayStr, silent: true }))
      .unwrap()
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNaerasPerk, yesterdayStr]);

  useEffect(() => {
    const timers = adjustTimers.current;

    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  /** The days currently on screen: server rows with unsaved edits on top. */
  const activeOffers = useMemo(
    () =>
      selectedDates
        .map((date) => drafts[date] ?? (bidsMap[date] ? bidToDayOffer(bidsMap[date]) : null))
        .filter((o): o is DayOffer => Boolean(o)),
    [selectedDates, drafts, bidsMap]
  );

  const currentEditingOffer =
    activeOffers.find((o) => o.date === activeAdjusterDate) ?? activeOffers[0];

  /** Any day's offer, selected or not, with unsaved edits on top. */
  const offerFor = (date: string): DayOffer | undefined =>
    drafts[date] ?? (bidsMap[date] ? bidToDayOffer(bidsMap[date]) : undefined);

  const missingDates = selectedDates.filter((date) => !bidsMap[date]);
  const hasAnyCalculated = selectedDates.some((date) => Boolean(bidsMap[date]));
  const allCalculated = selectedDates.length > 0 && missingDates.length === 0;

  const failedDates = missingDates
    .map((date) => ({ date, status: dateStatuses[date] }))
    .filter(
      (entry): entry is { date: string; status: { state: 'error'; message: string } } =>
        entry.status?.state === 'error'
    )
    .map(({ date, status }) => ({ date, message: status.message }));

  // Any selected day with no bid that isn't still loading and didn't fail for
  // some other reason. Derived from the absence of the row rather than from a
  // parsed error code, so the hint still shows if the 404 body ever changes
  // shape — a day with nothing to display must never look like a blank page.
  const uncalculatedDates = missingDates.filter(
    (date) =>
      dateStatuses[date]?.state !== 'loading' && dateStatuses[date]?.state !== 'error'
  );

  /**
   * The days finalize would accept: everything not locked yet, plus locked days
   * carrying a pending revision. A locked day without one is rejected and takes
   * the whole call down with it, so it must never be sent.
   */
  const finalizableDates = activeOffers
    .filter((offer) => !offer.isFinalized || offer.hasPendingRevision)
    .map((offer) => offer.date);

  /** Locked days holding a newer Excel that only a second finalize applies. */
  const pendingRevisionOffers = activeOffers.filter((offer) => offer.hasPendingRevision);

  /** Locked days whose hybrid was moved by a revision of an older day. */
  const correctedOffers = activeOffers.filter(
    (offer) => offer.isFinalized && offer.hybridCorrectionMwh !== 0
  );

  /**
   * Tomorrow, still unlocked. The 23:30 job will lock whatever draft it has, so
   * the operator is told that in advance rather than discovering it the next
   * morning as an `auto_finalized` badge.
   */
  const autoFinalizeOffer = calendar
    ? activeOffers.find((offer) => offer.date === calendar.min_date && !offer.isFinalized)
    : undefined;

  const handleDatesChange = (dates: string[]) => {
    setSelectedDates(dates);
  };

  /**
   * Saves the adjusted program. The +/− presses land locally first so the
   * controls stay responsive, then one PATCH carries the whole program.
   */
  const persistAdjustment = useCallback(
    (offer: DayOffer, saved: TNaerasBid | undefined) => {
      // Only what moved goes out: the server checks the hours a request
      // changes, so an untouched out-of-range hour can't block a hybrid edit
      const payload = adjustPayload(offer, saved);

      // Back to the saved values: nothing to send, and the saved row (with
      // its own warnings) is what the screen should show again
      if (Object.keys(payload).length === 0) {
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[offer.date];
          return next;
        });
        return;
      }

      dispatch(adjustNaerasBidAsync({ targetDate: offer.date, payload, silent: true }))
        .unwrap()
        .then((response) => {
          setDrafts((prev) => {
            const next = { ...prev };
            if (offer.status === 'final') {
              if (next[offer.date]) {
                next[offer.date] = {
                  ...next[offer.date],
                  hasPendingRevision: response.has_pending_revision || true,
                  // Each block's max depends on the other, so take it fresh
                  limits: response.limits,
                  hybridWarnings: response.warnings ?? [],
                };
              }
            } else {
              delete next[offer.date];
            }
            return next;
          });
        })
        .catch((error) => {
          // A limit violation answers with every broken rule in `extra.errors`,
          // and each one names the hour or block at fault — that list is the
          // only thing that tells the operator which press was refused, so the
          // message is built here instead of leaving it to the global toast.
          showToast({
            type: 'error',
            title: 'Η μικροαλλαγή απορρίφθηκε',
            message: naerasWriteErrorMessage(
              error,
              'Η αλλαγή δεν αποθηκεύτηκε.'
            ),
          });
        });
    },
    [dispatch, showToast]
  );

  const handleOfferChange = (updatedOffer: DayOffer) => {
    setDrafts((prev) => ({ ...prev, [updatedOffer.date]: updatedOffer }));

    clearTimeout(adjustTimers.current[updatedOffer.date]);
    adjustTimers.current[updatedOffer.date] = setTimeout(
      () => persistAdjustment(updatedOffer, bidsMap[updatedOffer.date]),
      ADJUST_DEBOUNCE_MS
    );
  };

  /**
   * Asks NAERAS for the selected days. One call covers all of them and runs
   * the whole pipeline, so it is slow and must not be fired twice.
   */
  const handleCalculate = async () => {
    if (selectedDates.length === 0 || isCalculating) return;

    const recalculate = allCalculated;

    if (recalculate) {
      // `adjusted` is set by a PATCH or an uploaded Excel — exactly what
      // recalculate: true throws away
      const editedDates = selectedDates.filter(
        (date) => drafts[date] || bidsMap[date]?.adjusted
      );
      const confirmed = window.confirm(
        editedDates.length
          ? `Ο επανυπολογισμός διαγράφει τις αλλαγές σας στις ${editedDates
              .map((date) => format(parseISO(date), 'dd/MM'))
              .join(', ')} (από το UI ή από Excel). Συνέχεια;`
          : 'Οι επιλεγμένες ημέρες έχουν ήδη υπολογιστεί και θα υπολογιστούν ξανά με τα τελευταία δεδομένα. Συνέχεια;'
      );

      if (!confirmed) return;
    }

    setIsCalculating(true);

    try {
      const response = await dispatch(
        calculateNaerasBidsAsync({
          payload: { dates: selectedDates, recalculate },
          silent: true,
        })
      ).unwrap();

      setDrafts({});
      setDateStatuses((prev) => {
        const next = { ...prev };
        response.items.forEach((bid) => {
          next[bid.target_date] = { state: 'loaded' };
        });

        return next;
      });

      if (response.warning) {
        showToast({ type: 'warning', title: 'Προσοχή', message: response.warning });
      } else {
        showToast({
          type: 'success',
          title: 'Υπολογισμός Προσφοράς',
          message: `Υπολογίστηκαν ${response.items.length} ημέρες.`,
        });
      }
    } catch (error) {
      // Nothing was saved — a single locked or out-of-range day takes the whole
      // call with it, so the recalculation the operator just confirmed did not
      // happen to any of the days. Reported here rather than by the global
      // toast, which would only repeat the server's own sentence.
      showToast({
        type: 'error',
        title: 'Ο υπολογισμός δεν έγινε',
        message: naerasWriteErrorMessage(error, 'Καμία ημέρα δεν υπολογίστηκε.'),
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);

    try {
      await Promise.all([
        dispatch(getNaerasCalendarAsync()).unwrap(),
        dispatch(getNaerasPoolAsync()).unwrap().catch(() => undefined),
        ...selectedDates.map((date) => loadBid(date)),
      ]);
      setDrafts({});
    } catch {
      // Reported by the global error handler.
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Locks the given days.
   *
   * The server takes each day's current draft — the uploaded file if there is
   * one, otherwise the adjustments, otherwise the calculated values — so this is
   * the same call whether the operator uploaded anything or not. A day that is
   * already final is only accepted when a newer Excel is waiting for it, in
   * which case finalizing again is a revision and may shift the hybrid of
   * another day; that day is refetched so the screen doesn't keep a stale row.
   *
   * Rejections are reported here rather than by the global handler: the call is
   * all-or-nothing, and the operator needs to know nothing was locked.
   */
  const finalizeDates = useCallback(
    async (dates: string[]) => {
      if (dates.length === 0 || isFinalizing) return false;

      setIsFinalizing(true);

      try {
        const response = await dispatch(
          finalizeNaerasBidsAsync({ payload: { dates }, silent: true })
        ).unwrap();

        setDrafts({});

        const { revisions, correction } = response;
        const recalculated = response.recalculated ?? [];

        setRecalculatedDates(recalculated);

        if (correction) {
          // It is not in `items`, so the store still holds its old values.
          await dispatch(
            getNaerasBidAsync({ targetDate: correction.target_date, silent: true })
          )
            .unwrap()
            .catch(() => undefined);
        }

        if (revisions.length) {
          const revised = revisions
            .map(
              (revision) =>
                `${format(parseISO(revision.target_date), 'dd/MM')}: ${
                  revision.error_mwh >= 0 ? '+' : ''
                }${revision.error_mwh.toFixed(1)} MWh εκροή`
            )
            .join(', ');
          const correctionText = correction
            ? ` Η υβριδική της ${format(parseISO(correction.target_date), 'dd/MM')} διορθώθηκε κατά ${correction.delta_mwh.toFixed(1)} MWh.`
            : '';

          showToast({
            type: 'warning',
            title: 'Αναθεώρηση προσφοράς',
            message: `Αναθεωρήθηκε — ${revised}.${correctionText}`,
          });
        } else {
          showToast({
            type: 'success',
            title: 'Οριστικοποίηση Προσφοράς',
            message: recalculated.length
              ? `Οριστικοποιήθηκαν ${response.items.length} ημέρες. Υπολογίστηκαν ξανά πριν κλειδώσουν: ${recalculated
                  .map((date) => format(parseISO(date), 'dd/MM'))
                  .join(', ')}.`
              : `Οριστικοποιήθηκαν ${response.items.length} ημέρες.`,
          });
        }

        return true;
      } catch (error) {
        showToast({
          type: 'error',
          title: 'Η οριστικοποίηση απέτυχε',
          message: naerasWriteErrorMessage(
            error,
            'Καμία ημέρα δεν οριστικοποιήθηκε.'
          ),
        });

        return false;
      } finally {
        setIsFinalizing(false);
      }
    },
    [dispatch, isFinalizing, showToast]
  );

  /** Downloads the workbook the operator sends to ΔΕΔΔΗΕ, then prompts. */
  const handleExportExcel = async () => {
    if (selectedDates.length === 0 || isExporting) return;

    if (!allCalculated) {
      showToast({
        type: 'warning',
        title: 'Δεν υπάρχει προσφορά',
        message: 'Υπολογίστε πρώτα την προσφορά για όλες τις επιλεγμένες ημέρες.',
      });
      return;
    }

    setIsExporting(true);

    try {
      const blob = await dispatch(exportNaerasBidsAsync({ dates: selectedDates })).unwrap();

      downloadBlob(blob as Blob, exportFileName(selectedDates));

      showToast({
        type: 'info',
        title: 'Εξαγωγή σε Excel',
        message: 'Το αρχείο προσφοράς για τον ΔΕΔΔΗΕ δημιουργήθηκε επιτυχώς.',
      });

      setIsExcelDialogOpen(true);
    } catch {
      // Reported by the global error handler.
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Uploads the file that was actually sent, then locks the days it covers.
   *
   * The upload alone changes nothing that counts: the server stores it as a
   * draft (or, for a day already final, as a pending revision) and waits for
   * finalize. The two steps are chained here so the operator's single action
   * still ends with the days locked, and the file's own dates are used rather
   * than the selection — the workbook decides which days it is for.
   *
   * Limit breaches come back as warnings and do not block; they are shown after
   * the lock so the operator sees what was recorded outside the limits.
   */
  const handleImportFinalExcel = async (file: File) => {
    let warnings: string[] = [];
    let dates: string[] = [];

    try {
      const response = await dispatch(
        importNaerasBidsAsync({ file, silent: true })
      ).unwrap();

      setDrafts({});
      warnings = response.warnings ?? [];
      dates = response.items.map((bid) => bid.target_date);
    } catch (error) {
      throw new Error(
        naerasWriteErrorMessage(error, 'Η μεταφόρτωση του οριστικού Excel απέτυχε.')
      );
    }

    const finalized = await finalizeDates(dates);

    if (warnings.length) {
      showToast({
        type: 'warning',
        title: finalized
          ? 'Οριστικοποίηση με παρατηρήσεις'
          : 'Το αρχείο αποθηκεύτηκε με παρατηρήσεις',
        message: warnings.join(' '),
      });
    }

    if (!finalized) {
      // The values are stored either way, so this is not a lost upload — but the
      // days are not locked, and saying so is the whole point of the dialog.
      throw new Error(
        'Το αρχείο αποθηκεύτηκε, αλλά οι ημέρες δεν οριστικοποιήθηκαν. Δοκιμάστε «Οριστικοποίηση» ξανά.'
      );
    }
  };

  /**
   * Locks the exported days with the values that were exported, no file.
   *
   * Only the days finalize would accept are sent: a day already locked without a
   * pending revision is refused and would take the whole call with it, so a
   * selection that mixes locked and open days must not be passed through whole.
   */
  const handleConfirmWithoutFile = () => finalizeDates(finalizableDates);

  const handleConfigureChart = () => {
    navigate('/chart-config');
  };

  // Aggregated analytics metrics for selected days (all in MWh)
  const sumHydro = (offers: DayOffer[]) =>
    offers.reduce(
      (acc, o) => acc + o.hourly.reduce((s, h) => s + h.mysAno + h.mysKato, 0),
      0
    );
  const sumHybrid = (offers: DayOffer[]) =>
    offers.reduce((acc, o) => acc + o.hybrid1st12h + o.hybrid2nd12h, 0);
  const sumAno = (offers: DayOffer[]) =>
    offers.reduce((acc, o) => acc + o.hourly.reduce((s, h) => s + h.mysAno, 0), 0);

  const yesterdayBid: TNaerasBid | undefined = bidsMap[yesterdayStr];
  const yesterdayOffers = yesterdayBid ? [bidToDayOffer(yesterdayBid)] : [];
  // One day against one day, so a multi-day selection is averaged per day.
  const perDay = (total: number) => (activeOffers.length ? total / activeOffers.length : 0);

  const totalHydroMwh = sumHydro(activeOffers);
  const totalHybridMwh = sumHybrid(activeOffers);
  const totalProesperasMwh = sumAno(activeOffers);

  const hydroChange = changeAgainst(
    perDay(totalHydroMwh),
    yesterdayOffers.length ? sumHydro(yesterdayOffers) : null,
    yesterdayLabel
  );
  const proesperasChange = changeAgainst(
    perDay(totalProesperasMwh),
    yesterdayOffers.length ? sumAno(yesterdayOffers) : null,
    yesterdayLabel
  );

  /**
   * How much the hybrid pool can be trusted.
   *
   * A reconciled or hand-set value is healthy: `stale` means Job A hasn't
   * settled yet and `seed` means there is no history at all, so neither
   * deserves the green upward arrow that would otherwise make an unconfigured
   * 0 MWh read as a real, current figure.
   */
  const poolStatus = !pool
    ? { label: 'Μη διαθέσιμο', changeType: 'negative' as const }
    : {
        label: poolSourceLabel(pool),
        changeType:
          pool.source === 'reconciled' || pool.source === 'manual'
            ? ('positive' as const)
            : ('negative' as const),
      };

  const quickAnalytics: QuickAnalytic[] = [
    {
      title: 'Υδροηλεκτρική Προσφορά',
      value: totalHydroMwh.toFixed(1),
      unit: 'MWh',
      change: hydroChange.change,
      changeType: hydroChange.changeType,
      icon: Zap,
    },
    {
      title: 'Υβριδική Προσφορά',
      value: totalHybridMwh.toFixed(1),
      unit: 'MWh',
      change: `${activeOffers.length * 2} x 12ωρα`,
      changeType: 'positive',
      icon: Activity,
    },
    {
      title: 'Ταμιευτήρας Προεσπέρας',
      value: pool?.lower_available_mwh != null ? pool.lower_available_mwh.toFixed(1) : totalProesperasMwh.toFixed(1),
      unit: 'MWh',
      change: proesperasChange.change,
      changeType: proesperasChange.changeType,
      icon: TrendingUp,
    },
    {
      title: 'Απόθεμα Υβριδικής Ενέργειας',
      value: pool ? pool.value.toFixed(1) : '—',
      unit: 'MWh',
      change: poolStatus.label,
      changeType: poolStatus.changeType,
      icon: Calculator,
    },
  ];

  const allFinalized = activeOffers.length > 0 && activeOffers.every((o) => o.isFinalized);

  if (hasNaerasPerk === null) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;
  }

  if (hasNaerasPerk === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border p-8 text-center space-y-6">
          <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Δήλωση ΑΕΗ ΑΝ - ΥΕΕ</h2>
          <p className="text-gray-500">
            This feature requires the NAERAS add-on. Please contact your organization administrator or Enexan support to enable this feature for your account.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-xl transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30 px-6 py-8">
      <div className="mx-auto max-w-7xl space-y-8">
        {/* Finalization Status Banner */}
        {allFinalized && (
          <div className="rounded-3xl border border-emerald-200 bg-emerald-50/90 p-4 text-emerald-950 shadow-md backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-emerald-900">
                  {activeOffers.some((o) => o.revisedAt)
                    ? 'Η προσφορά έχει αναθεωρηθεί'
                    : 'Η προσφορά έχει οριστικοποιηθεί'}
                </h3>
                <p className="text-xs text-emerald-700">
                  Πηγή οριστικοποίησης:{' '}
                  <strong>
                    {activeOffers[0]?.finalSource === 'auto'
                      ? `Αυτόματη οριστικοποίηση ${AUTO_FINALIZE_TIME}`
                      : activeOffers[0]?.finalSource === 'excel'
                      ? `Εισαγωγή από οριστικό Excel (${activeOffers[0]?.originalExcelName})`
                      : 'Επιβεβαίωση υπολογισμένων τιμών από το UI'}
                  </strong>{' '}
                  | Ημερομηνία:{' '}
                  {activeOffers[0]?.finalizedAt
                    ? format(new Date(activeOffers[0].finalizedAt), 'dd/MM/yyyy HH:mm')
                    : ''}
                  {activeOffers[0]?.revisedAt
                    ? ` | Αναθεώρηση: ${format(
                        new Date(activeOffers[0].revisedAt),
                        'dd/MM/yyyy HH:mm'
                      )}`
                    : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* A newer Excel is sitting on a locked day and changes nothing until
            the day is finalized again — the one state the operator cannot see
            from the numbers on screen, since `final` still holds the old ones. */}
        {pendingRevisionOffers.length > 0 && (
          <div className="flex items-start gap-3 rounded-2xl border border-orange-200 bg-orange-50/90 p-4 text-orange-950 shadow-sm">
            <RotateCcw className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-600" />
            <div>
              <p className="text-sm font-semibold">
                Εκκρεμεί αναθεώρηση για {pendingRevisionOffers.length}{' '}
                {pendingRevisionOffers.length === 1 ? 'ημέρα' : 'ημέρες'}
              </p>
              <ul className="mt-0.5 space-y-0.5 text-xs text-orange-800">
                {pendingRevisionOffers.map((offer) => (
                  <li key={offer.date}>
                    {format(parseISO(offer.date), 'dd/MM/yyyy')}
                    {offer.originalExcelName ? ` — ${offer.originalExcelName}` : ''}
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-xs font-semibold text-orange-800">
                Οι τιμές που εμφανίζονται είναι ακόμη οι οριστικές. Πατήστε
                «Αναθεώρηση Προσφοράς» για να εφαρμοστεί το νέο αρχείο.
              </p>
            </div>
          </div>
        )}

        {/* A locked day whose hybrid was moved by the revision of an older day:
            what is final no longer matches the file that was uploaded for it. */}
        {correctedOffers.length > 0 && (
          <div className="flex items-start gap-3 rounded-2xl border border-indigo-200 bg-indigo-50/90 p-4 text-indigo-950 shadow-sm">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-600" />
            <div>
              <p className="text-sm font-semibold">
                Εφαρμόστηκε διόρθωση υβριδικής από αναθεώρηση παλαιότερης ημέρας
              </p>
              <ul className="mt-0.5 space-y-0.5 text-xs text-indigo-800">
                {correctedOffers.map((offer) => (
                  <li key={offer.date}>
                    {format(parseISO(offer.date), 'dd/MM/yyyy')}:{' '}
                    {offer.hybridCorrectionMwh >= 0 ? '+' : ''}
                    {offer.hybridCorrectionMwh.toFixed(1)} MWh
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-xs text-indigo-800">
                Η οριστική προσφορά διαφέρει από το αρχείο που είχε ανέβει, ώστε να
                συμφωνεί το απόθεμα του ταμιευτήρα.
              </p>
            </div>
          </div>
        )}

        {/* Tomorrow is still a draft: the nightly job will lock it as it stands. */}
        {autoFinalizeOffer && (
          <div className="flex items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50/90 p-4 text-sky-950 shadow-sm">
            <Clock className="mt-0.5 h-5 w-5 flex-shrink-0 text-sky-600" />
            <div>
              <p className="text-sm font-semibold">
                Η προσφορά της {format(parseISO(autoFinalizeOffer.date), 'dd/MM/yyyy')} θα
                οριστικοποιηθεί αυτόματα στις {AUTO_FINALIZE_TIME}
              </p>
              <p className="mt-0.5 text-xs text-sky-800">
                Θα κλειδωθούν οι τιμές που ισχύουν εκείνη την ώρα. Μπορείτε να
                ανεβάσετε το αρχείο που στάλθηκε και μετά.
              </p>
            </div>
          </div>
        )}

        {/* Finalize recalculates an unedited future day first (latest data,
            current wet-season switch), so the locked numbers can differ from
            what was on screen before the click. */}
        {recalculatedDates.length > 0 && (
          <div className="flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50/90 p-4 text-blue-950 shadow-sm">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-semibold">
                Υπολογίστηκαν ξανά κατά την οριστικοποίηση:{' '}
                {recalculatedDates.map((date) => format(parseISO(date), 'dd/MM/yyyy')).join(', ')}
              </p>
              <p className="mt-0.5 text-xs text-blue-800">
                Οι ημέρες χωρίς αλλαγές υπολογίζονται με τα τελευταία δεδομένα πριν κλειδώσουν, οπότε οι
                τιμές τους μπορεί να διαφέρουν από αυτές που εμφανίζονταν πριν.
              </p>
            </div>
            <button
              onClick={() => setRecalculatedDates([])}
              className="text-xs font-semibold text-blue-700 hover:underline"
            >
              Απόκρυψη
            </button>
          </div>
        )}

        {/* Page Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              ΝΑΕΡΑΣ Υβριδικό (Υπολογισμός Προσφορών)
            </h1>
            <p className="mt-1 text-base text-gray-600">
              Διαχείριση, επεξεργασία και υπολογισμός προσφορών υβριδικής ενέργειας
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* History Link Button */}
            <button
              onClick={() => navigate('/history')}
              className="flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50/70 px-4 py-2.5 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100"
            >
              <History className="h-4 w-4" />
              Ιστορικό Προσφορών
            </button>

            {/* Job status link — grouped with History as the two "go elsewhere"
                buttons, so the gradient ones stay the actions of this page. */}
            <button
              onClick={() => navigate('/job-runs')}
              className="flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50/70 px-4 py-2.5 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100"
            >
              <Activity className="h-4 w-4" />
              Κατάσταση Εργασιών
            </button>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Ανανέωση
            </button>

            {/* The switch is read by the next calculation, so it sits beside it */}
            <WetSeasonSwitch />

            {/* Calculate Button */}
            <button
              onClick={handleCalculate}
              disabled={isCalculating || selectedDates.length === 0}
              className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl disabled:opacity-60"
            >
              {isCalculating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Calculator className="h-4 w-4" />
              )}
              {isCalculating
                ? 'Υπολογισμός...'
                : allCalculated
                ? 'Επανυπολογισμός Προσφοράς'
                : 'Υπολογισμός Προσφοράς'}
            </button>

            {/* Finalize Button — the only thing that locks a day. Uploading the
                Excel just stores a draft, so this stays available afterwards. */}
            <button
              onClick={() => finalizeDates(finalizableDates)}
              disabled={isFinalizing || finalizableDates.length === 0}
              title={
                finalizableDates.length === 0
                  ? 'Δεν υπάρχει ημέρα προς οριστικοποίηση'
                  : `Κλειδώνει ${finalizableDates.length} ημέρες με τις τρέχουσες τιμές τους`
              }
              className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-amber-600/25 transition hover:from-amber-600 hover:to-orange-700 hover:shadow-xl disabled:opacity-60"
            >
              {isFinalizing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : pendingRevisionOffers.length > 0 ? (
                <RotateCcw className="h-4 w-4" />
              ) : (
                <Lock className="h-4 w-4" />
              )}
              {pendingRevisionOffers.length > 0
                ? 'Αναθεώρηση Προσφοράς'
                : 'Οριστικοποίηση'}
            </button>

            {/* Export to Excel Button */}
            <button
              onClick={handleExportExcel}
              disabled={isExporting || !allCalculated}
              className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-600/25 transition hover:from-emerald-700 hover:to-teal-700 hover:shadow-xl disabled:opacity-60"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4" />
              )}
              Εξαγωγή σε Excel ΔΕΔΔΗΕ
            </button>
          </div>
        </div>

        {/* 1. Calendar Selection Section */}
        <section>
          <OfferCalendar
            selectedDates={selectedDates}
            onDatesChange={handleDatesChange}
            calendar={calendar}
          />
        </section>

        {/* 2. Quick KPI Analytics Bar Moved Directly Below Calendar */}
        <section>
          <QuickAnalytics analytics={quickAnalytics} />
        </section>

        {/* Days with no bid yet — a state of the data, not a failure */}
        {uncalculatedDates.length > 0 && (
          <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/90 p-4 text-amber-900 shadow-sm">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-semibold">
                {hasAnyCalculated
                  ? `Δεν έχει υπολογιστεί προσφορά για ${uncalculatedDates.length} από τις επιλεγμένες ημέρες.`
                  : 'Δεν έχει υπολογιστεί προσφορά για τις επιλεγμένες ημέρες.'}
              </p>
              <ul className="mt-0.5 space-y-0.5 text-xs text-amber-800">
                {uncalculatedDates.map((date) => {
                  const status = dateStatuses[date];

                  return (
                    <li key={date}>
                      {format(parseISO(date), 'dd/MM/yyyy')}
                      {status?.state === 'missing' && status.message ? ` — ${status.message}` : ''}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-1.5 text-xs font-semibold text-amber-800">
                Πατήστε «Υπολογισμός Προσφοράς» για να ζητηθούν από το NAERAS.
              </p>
            </div>
          </div>
        )}

        {/* Days that failed to load for any other reason */}
        {failedDates.length > 0 && (
          <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50/90 p-4 text-rose-900 shadow-sm">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-600" />
            <div className="min-w-0">
              <p className="text-sm font-semibold">Αποτυχία φόρτωσης προσφοράς</p>
              <ul className="mt-0.5 space-y-0.5 text-xs text-rose-800">
                {failedDates.map(({ date, message }) => (
                  <li key={date}>
                    {format(parseISO(date), 'dd/MM/yyyy')}: {message}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => failedDates.forEach(({ date }) => loadBid(date))}
                className="mt-2 rounded-lg bg-rose-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-rose-700"
              >
                Επανάληψη
              </button>
            </div>
          </div>
        )}

        {/* 3. Multi-Day Offer Visualizations Section */}
        <section>
          <MultiDayOfferCharts
            offers={activeOffers}
            onConfigureChart={handleConfigureChart}
          />
        </section>

        {/* Section Separator */}
        <div className="my-10">
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            <h2 className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-lg font-bold text-gray-800 shadow-sm">
              Επεξεργασία Προσφοράς
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>
        </div>

        {/* Day Selection Bar for Adjuster if > 1 day selected */}
        {activeOffers.length > 1 && (
          <div className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm">
            <span className="text-xs font-bold text-gray-600">Επιλογή ημέρας για επεξεργασία:</span>
            <div className="flex flex-wrap gap-2">
              {activeOffers.map((o) => (
                <button
                  key={o.date}
                  onClick={() => setActiveAdjusterDate(o.date)}
                  className={`rounded-xl px-4 py-1.5 text-xs font-bold transition ${
                    activeAdjusterDate === o.date
                      ? 'bg-indigo-600 text-white shadow'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {o.dayLabel}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 4. Offer Micro-adjustments Section */}
        {currentEditingOffer && (
          <section>
            <OfferAdjuster
              offer={currentEditingOffer}
              allSelectedOffers={activeOffers}
              poolBudget={dayPoolBudget(currentEditingOffer, calendar?.min_date, offerFor)}
              onOfferChange={handleOfferChange}
            />
          </section>
        )}
      </div>

      {/* Final Excel Dialog Prompt */}
      <FinalExcelDialog
        isOpen={isExcelDialogOpen}
        onClose={() => setIsExcelDialogOpen(false)}
        onImportFile={handleImportFinalExcel}
        onConfirmWithoutFile={handleConfirmWithoutFile}
      />
    </div>
  );
};

export default CalculatePage;
